import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import HomeScreen from "../pages/HomeScreen";
import MercatScreen from "../pages/MercatScreen";
import ProfileScreen from "../pages/ProfileScreen";
import { useLang } from "../lib/i18n";
import { useUnread } from "../context/UnreadContext";

export const SCREENS = ["/", "/mercat", "/perfil"];
const NO_SWIPE =
  '[data-no-swipe],[role="dialog"],[role="listbox"],[role="menu"],[data-radix-popper-content-wrapper],input,textarea,select,[contenteditable="true"]';
const EDGE = 20; // iOS/Android back-forward gesture zone
const HINT_KEY = "mm_swipe_hint_seen";

const pointOf = (evt) => evt.touches?.[0] || evt.changedTouches?.[0] || evt;
const blocked = (evt) => {
  const { clientX } = pointOf(evt);
  if (typeof clientX === "number" && (clientX < EDGE || clientX > window.innerWidth - EDGE)) return true;
  return !!evt.target?.closest?.(NO_SWIPE);
};

// Embla handles direction lock, finger-following, edge resistance and flicks.
// We add a lower distance threshold (min(50px, 20% width)) on top of it.
function useLowThreshold(embla, viewportRef) {
  useEffect(() => {
    const el = viewportRef.current;
    if (!embla || !el) return undefined;
    let g = null;
    const start = (e) => {
      const p = e.touches[0];
      g = blocked(e) ? null : { x: p.clientX, y: p.clientY, t: Date.now(), from: embla.selectedScrollSnap(), lx: p.clientX, ly: p.clientY };
    };
    const move = (e) => {
      if (!g) return;
      g.lx = e.touches[0].clientX;
      g.ly = e.touches[0].clientY;
    };
    const end = () => {
      const s = g;
      g = null;
      if (!s) return;
      const dx = s.lx - s.x;
      const dy = s.ly - s.y;
      if (Math.abs(dx) <= Math.abs(dy) * 1.2) return;
      const fast = Math.abs(dx) / Math.max(1, Date.now() - s.t) > 0.45 && Math.abs(dx) > 30;
      if (Math.abs(dx) < Math.min(50, window.innerWidth * 0.2) && !fast) return;
      setTimeout(() => {
        if (embla.selectedScrollSnap() !== s.from) return;
        const target = s.from + (dx < 0 ? 1 : -1);
        if (target >= 0 && target < SCREENS.length) embla.scrollTo(target);
      }, 0);
    };
    const cancel = () => (g = null);
    el.addEventListener("touchstart", start, { passive: true, capture: true });
    el.addEventListener("touchmove", move, { passive: true, capture: true });
    el.addEventListener("touchend", end, { passive: true, capture: true });
    el.addEventListener("touchcancel", cancel, { passive: true, capture: true });
    return () => {
      el.removeEventListener("touchstart", start, { capture: true });
      el.removeEventListener("touchmove", move, { capture: true });
      el.removeEventListener("touchend", end, { capture: true });
      el.removeEventListener("touchcancel", cancel, { capture: true });
    };
  }, [embla, viewportRef]);
}

export default function SwipeShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const index = Math.max(0, SCREENS.indexOf(location.pathname));
  const [selected, setSelected] = useState(index);
  const [hint, setHint] = useState(() => !localStorage.getItem(HINT_KEY));
  const dismissHint = useCallback(() => {
    localStorage.setItem(HINT_KEY, "1");
    setHint(false);
  }, []);
  useEffect(() => {
    if (!hint) return undefined;
    const id = setTimeout(dismissHint, 4500);
    return () => clearTimeout(id);
  }, [hint, dismissHint]);
  const viewportRef = useRef(null);
  const wheel = useRef({ acc: 0, lock: 0 });
  const watchDrag = useCallback((_api, evt) => !blocked(evt), []);
  const [emblaRef, embla] = useEmblaCarousel({ startIndex: index, loop: false, dragThreshold: 10, duration: 24, watchDrag });
  const setViewport = useCallback((node) => { viewportRef.current = node; emblaRef(node); }, [emblaRef]);
  useLowThreshold(embla, viewportRef);

  const go = useCallback((i) => {
    if (i >= 0 && i < SCREENS.length && SCREENS[i] !== window.location.pathname) navigate(SCREENS[i]);
  }, [navigate]);

  // Embla → URL (push, so browser back returns to the previous screen)
  useEffect(() => {
    if (!embla) return undefined;
    const onSelect = () => {
      const i = embla.selectedScrollSnap();
      setSelected(i);
      if (i !== 0) dismissHint();
      go(i);
    };
    embla.on("select", onSelect);
    return () => {
      embla.off("select", onSelect);
    };
  }, [embla, go, dismissHint]);

  // URL (back/forward, dots, arrows, keyboard) → Embla
  useEffect(() => {
    setSelected(index);
    if (embla && embla.selectedScrollSnap() !== index) embla.scrollTo(index);
  }, [embla, index]);

  useEffect(() => {
    const onKey = (e) => {
      if (document.querySelector('[role="dialog"]') || e.target.closest?.("input,textarea,select,[contenteditable]")) return;
      if (e.key === "ArrowRight") go(index + 1);
      if (e.key === "ArrowLeft") go(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, index]);

  const onWheel = (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || e.target.closest(NO_SWIPE)) return;
    const w = wheel.current;
    if (Date.now() < w.lock) return;
    w.acc += e.deltaX;
    if (Math.abs(w.acc) > 90) {
      go(index + (w.acc > 0 ? 1 : -1));
      w.acc = 0;
      w.lock = Date.now() + 700;
    }
  };

  const screens = [HomeScreen, MercatScreen, ProfileScreen];
  return (
    <div className="fixed inset-0 overflow-hidden bg-white swipe-root" onWheel={onWheel} data-testid="swipe-shell" data-screen={selected}>
      <div ref={setViewport} className="h-full overflow-hidden swipe-viewport" data-testid="swipe-viewport">
        <div className="flex h-full">
          {screens.map((Screen, i) => (
            <section
              key={SCREENS[i]}
              aria-hidden={i !== selected}
              className="h-full min-w-0 shrink-0 grow-0 basis-full overflow-y-auto overflow-x-hidden overscroll-contain pane"
              data-testid={`screen-${["home", "mercat", "profile"][i]}`}
            >
              <Screen active={i === index} />
            </section>
          ))}
        </div>
      </div>
      <PageDots index={selected} go={go} />
      <EdgeArrows index={index} go={go} t={t} />
      {hint && selected === 0 && <SwipeHint t={t} />}
    </div>
  );
}

function SwipeHint({ t }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 z-30 flex justify-center swipe-hint-pos" data-testid="swipe-hint">
      <span className="swipe-hint rounded-full bg-navy/90 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur">
        {t("nav.swipeHint")}
      </span>
    </div>
  );
}

function PageDots({ index, go }) {
  const { unread } = useUnread();
  return (
    <div className="pointer-events-none fixed inset-x-0 z-30 flex justify-center bottom-safe">
      <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-white/70 px-2.5 py-1.5 shadow-sm backdrop-blur-md" data-testid="page-dots">
        {SCREENS.map((s, i) => (
          <button
            key={s}
            aria-label={s}
            aria-current={i === index}
            onClick={() => go(i)}
            data-testid={`page-dot-${i}`}
            className={`relative h-1.5 rounded-full transition-[width,background-color] duration-300 ${
              i === index ? "w-4 bg-brand" : "w-1.5 bg-navy/25"
            }`}
          >
            {i === 2 && unread.total > 0 && (
              <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-alert ring-1 ring-white" data-testid="page-dot-unread" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function EdgeArrows({ index, go, t }) {
  const cls =
    "fixed top-1/2 z-30 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 text-navy shadow-md backdrop-blur transition-[opacity,transform] duration-200 hover:scale-110 md:flex opacity-50 hover:opacity-100";
  return (
    <>
      {index > 0 && (
        <button className={`${cls} left-3`} onClick={() => go(index - 1)} aria-label={t("nav.prev")} data-testid="edge-arrow-prev">
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {index < SCREENS.length - 1 && (
        <button className={`${cls} right-3`} onClick={() => go(index + 1)} aria-label={t("nav.next")} data-testid="edge-arrow-next">
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
    </>
  );
}
